// // ReportLayoutUtils.js

// export const parseIfString = (value) => {
//   if (typeof value !== "string") return value;
//   try {
//     return JSON.parse(value);
//   } catch {
//     return value; // not JSON — leave as-is; the recursive walk below will
//                    // just see a non-object and stop, same as "no layout".
//   }
// };

// const normalizeDynamicValues = (dynamicValues) => {
//   if (
//     dynamicValues &&
//     typeof dynamicValues === "object" &&
//     !Array.isArray(dynamicValues) &&
//     dynamicValues.values &&
//     typeof dynamicValues.values === "object"
//   ) {
//     return dynamicValues.values;
//   }
//   return dynamicValues;
// };

// export const collectReportExtras = (rawLayout, rawDynamicValues, found = []) => {
//   const layout = parseIfString(rawLayout);
//   const dynamicValues = normalizeDynamicValues(parseIfString(rawDynamicValues));
//   return walkReportExtras(layout, dynamicValues, found);
// };

// const walkReportExtras = (node, dynamicValues, found) => {
//   if (!node || typeof node !== "object") return found;
//   if (Array.isArray(node)) {
//     node.forEach((n) => walkReportExtras(n, dynamicValues, found));
//     return found;
//   }
//   if (node.component && (node.showOnReport === true || node.showOnReport === "true")) {
//     found.push({
//       id: node.id,
//       label: node.reportLabel || node.label || node.text || node.id,
//       value: dynamicValues?.[node.id] ?? "",
//     });
//   }
//   Object.values(node).forEach((v) => walkReportExtras(v, dynamicValues, found));
//   return found;
// };

// ReportLayoutUtils.js
// ---------------------------------------------------------------------------
// Shared between MakeReport.js (live authoring) and PatientReport.js
// (printing an already-saved report) so both walk the same screenDefinition
// shape the same way — one copy of the logic, no drift between call sites.
// ---------------------------------------------------------------------------

// Some backends serialize JSON columns (screenDefinition / reportData) as a
// JSON *string* rather than a parsed object/map, depending on how the column
// is mapped (e.g. a plain TEXT column vs. a JSONB type with a proper Java
// type mapping). Whether it's already an object or still a string, this
// normalizes it so callers don't have to care which one they got.
export const parseIfString = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value; // not JSON — leave as-is; the recursive walk below will
                   // just see a non-object and stop, same as "no layout".
  }
};

// MakeReport.js's own submit handler saves dynamicValues wrapped as
// { values: {...}, actions: {...} } (see handleSubmit's `reportData`).
// The live React state passed while authoring is the flat map itself, but
// once it round-trips through the backend and comes back for printing a
// saved report, it's wrapped. Rather than making every caller remember to
// unwrap it, normalize it once, here, for everyone.
const normalizeDynamicValues = (dynamicValues) => {
  if (
    dynamicValues &&
    typeof dynamicValues === "object" &&
    !Array.isArray(dynamicValues) &&
    dynamicValues.values &&
    typeof dynamicValues.values === "object"
  ) {
    return dynamicValues.values;
  }
  return dynamicValues;
};

// ---------------------------------------------------------------------------
// visibleWhen-style gating, reused for the new "reportVisibleWhen" field.
// Supports the two shapes already used elsewhere in the layout JSON:
//   { field: "organismIsolated", equals: "yes" }
//   { field: "printAntibiotic", truthy: true }
// ---------------------------------------------------------------------------
const isVisible = (visibleWhen, dynamicValues) => {
  if (!visibleWhen) return true;
  const val = dynamicValues?.[visibleWhen.field];
  if ("equals" in visibleWhen) return val === visibleWhen.equals;
  if ("truthy" in visibleWhen) return !!val === visibleWhen.truthy;
  return true;
};

// ---------------------------------------------------------------------------
// "reportValueMap" lets a component remap its raw stored value to display
// text before it hits the report — e.g. RADIO_GROUP "organismIsolated"
// mapping "no" -> "No Growth of Micro Organism", or "yes" -> the value of
// a *different* field via the "@fieldId" token (e.g. "@organismName").
// Falls back to the raw value when there's no mapping for it.
// ---------------------------------------------------------------------------
const resolveReportValue = (node, dynamicValues) => {
  const raw = dynamicValues?.[node.id];
  const map = node.reportValueMap;
  if (map && Object.prototype.hasOwnProperty.call(map, raw)) {
    const mapped = map[raw];
    if (typeof mapped === "string" && mapped.startsWith("@")) {
      return dynamicValues?.[mapped.slice(1)] ?? "";
    }
    return mapped;
  }
  return raw;
};

// Recursively walk the layout JSON and collect every component flagged
// "showOnReport": true/"true" — a generic way to surface arbitrary
// dynamic-screen values (e.g. the PBF comment editor) onto the printed
// report without the print component needing to know that field exists.
// Value comes from dynamicValues[component.id]; label comes from
// reportLabel (falling back to label/text/id).
//
// `dataSources` is an optional map of resolved datasource lists
// (e.g. { AntibioticByMainTest: [{ value, label }, ...] }), needed for
// components whose reportable content isn't one static value but a whole
// list pulled from a datasource (DATASOURCE_INPUT_GRID /
// REPORT_ONLY_DATASOURCE_LIST) — each item in the list becomes its own
// entry in the returned array.
export const collectReportExtras = (rawLayout, rawDynamicValues, dataSources = {}, found = []) => {
  const layout = parseIfString(rawLayout);
  const dynamicValues = normalizeDynamicValues(parseIfString(rawDynamicValues));
  return walkReportExtras(layout, dynamicValues, dataSources, found);
};

const DATASOURCE_LIST_COMPONENTS = new Set(["DATASOURCE_INPUT_GRID", "REPORT_ONLY_DATASOURCE_LIST"]);

const walkReportExtras = (node, dynamicValues, dataSources, found) => {
  if (!node || typeof node !== "object") return found;
  if (Array.isArray(node)) {
    node.forEach((n) => walkReportExtras(n, dynamicValues, dataSources, found));
    return found;
  }

  const isFlaggedForReport = node.showOnReport === true || node.showOnReport === "true";

  if (node.component && isFlaggedForReport && isVisible(node.reportVisibleWhen, dynamicValues)) {
    if (DATASOURCE_LIST_COMPONENTS.has(node.component)) {
  const items = dataSources?.[node.datasource] || [];
  items.forEach((item) => {
    const fieldId = `${node.id}_${item.value}`;
    found.push({
      id: fieldId,
      label: item.label,
      value: node.namesOnly ? "" : (dynamicValues?.[fieldId] ?? ""),
      alwaysShow: !!node.namesOnly,
      group: node.id,                              // NEW
      groupColumns: node.columns || 2,              // NEW
    });
  });
} else {
      found.push({
        id: node.id,
        label: node.reportLabel || node.label || node.text || node.id,
        value: resolveReportValue(node, dynamicValues),
        alwaysShow: false,
      });
    }
  }

  // Datasource-list components have no meaningful children to recurse into
  // beyond what we already expanded above.
  if (!node.component || !DATASOURCE_LIST_COMPONENTS.has(node.component)) {
    Object.values(node).forEach((v) => walkReportExtras(v, dynamicValues, dataSources, found));
  }

  return found;
};

// ---------------------------------------------------------------------------
// Recursively walk the layout JSON and collect every distinct `datasource`
// value it references, regardless of where/how deep it appears. Previously
// lived as a private helper inside MakeReport.js; moved here so
// PatientReport.js can resolve the same set of datasources when reprinting
// a saved report, without duplicating the walk logic.
// ---------------------------------------------------------------------------
export const collectDatasources = (rawNode, found = new Set()) => {
  const node = parseIfString(rawNode);
  if (!node || typeof node !== "object") return found;
  if (Array.isArray(node)) {
    node.forEach((n) => collectDatasources(n, found));
    return found;
  }
  if (node.datasource) found.add(node.datasource);
  Object.values(node).forEach((v) => collectDatasources(v, found));
  return found;
};